/**
 * Parceiros comerciais (antes "Fornecedores"), DEMO-03 telas/Parceiros.tsx,
 * agora persistentes: o GRUPO comercial e as suas ENTIDADES/CNPJ (Entidade
 * faturadora). Uma mesma empresa pode ter vários PAPÉIS sem cadastro
 * duplicado (ex.: Fabricante + Distribuidor). No Financeiro, quem recebe um
 * pagamento é o FAVORECIDO; a OCF mantém o nome "Ordem de Compra ao Fornecedor".
 * Registros da Oportunidade no parceiro (B2): o MESMO registro exibido na Oportunidade.
 */
import { EditorRegraCobranca, QuadroRegraCobranca } from './Locacao.tsx';
import { regraCobrancaVazia, type RegraCobrancaLocacao } from '../../../backend/src/compartilhado/locacao.ts';
import { regraRecebimentoVazia, type RegraRecebimento } from '../../../backend/src/compartilhado/regra-recebimento.ts';
import { EditorRegraRecebimento, QuadroRegraRecebimento } from './RegraRecebimento.tsx';
import { useState } from 'react';
import { usePersonalizacao } from './personalizacao.tsx';
import { useRotuloTrilha } from '../componentes/trilha.tsx';

import { api, consulta } from '../api.ts';
import { useSessao } from '../sessao.tsx';
import { useAmbiente } from '../ambiente.tsx';
import { dataHora } from '../formatos.ts';
import { navegar } from '../rotas.ts';
import { Ajuda, CampoBusca, Estado, EstadoCarga, Modal, Nota, SeloSituacao, Th, useCarga, useOrdenacao } from './ui.tsx';
import { Icone } from '../componentes/icones.tsx';
import { CampoCnpj, SeletorUf, useCnpjExistente } from './campos.tsx';
import { EditorReajuste } from './Reajuste.tsx';
import { ModalNovoRegistro } from './Oportunidade.tsx';
import { BarraFiltros, SeloRegistro } from './filtros.tsx';
import type { OpcoesOportunidade, Registro } from './tiposOportunidade.ts';
import { dataBr, statusRegistro } from '../../../backend/src/compartilhado/oportunidades.ts';
import { rotuloStatus } from './tipos.ts';
import { CAMPOS_CONDICOES_LOCACAO, PAPEIS, REGRAS_COMISSAO, regraComissao, textoReajuste, type CampoCondicao, type IdRegraComissao, type Papel, type Reajuste } from '../../../backend/src/compartilhado/comercial.ts';

export interface EntidadeParceiro {
  id: string;
  parceiro_id: string;
  cnpj: string;
  razao_social: string;
  nome_fantasia: string | null;
  ie: string | null;
  uf: string;
  endereco: string | null;
  codigo_revenda: string | null;
  contato: string | null;
  email: string | null;
  telefone: string | null;
  condicoes: string | null;
  status: 'ativo' | 'inativo';
  origem_sistema?: string | null;
  atualizado_em: string;
}
export type Parceiro = {
  id: string;
  nome: string;
  papeis: Papel[];
  status: 'ativo' | 'inativo';
  observacoes: string | null;
  comissao_regra: IdRegraComissao | null;
  comissao_percentual: number | null;
  reajuste_padrao: Reajuste | null;
  /** P3-28: regra de cobrança da Locação (null = não cadastrada; campos nulos = A confirmar) */
  regra_cobranca?: RegraCobrancaLocacao | null;
  /** Parametrização (0048): regra de recebimento da comissão (campos nulos = A confirmar) */
  regra_recebimento?: RegraRecebimento;
  /** preenchido só em parceiro migrado: edição exige justificativa */
  origem_sistema?: string | null;
  atualizado_em: string;
  entidades: EntidadeParceiro[];
} & { [K in `cond_${CampoCondicao}`]: string | null };

export const pctNum = (v: number) => `${v.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

type ColParceiro = 'nome' | 'papeis' | 'entidades' | 'cnpj' | 'contato' | 'comissao' | 'situacao';

/** F1-42: Parceiros comerciais no padrão das listas — trilha como título, filtros progressivos, ordenação pelo cabeçalho. */
export function ListaParceiros() {
  const { pode } = useSessao();
  const [busca, setBusca] = useState('');
  const [papel, setPapel] = useState<'' | Papel>('');
  const [situacao, setSituacao] = useState<'' | 'ativo' | 'inativo'>('ativo');
  const [novo, setNovo] = useState(false);
  const ord = useOrdenacao<ColParceiro>('nome');
  // P2-03/P2-04 (D7)
  const pers = usePersonalizacao('parceiros', { busca: { valor: busca, definir: setBusca },
    filtros: { papel, situacao },
    aplicarFiltros: (e: Record<string, unknown>) => (setPapel((e.papel as '' | Papel) ?? ''), setSituacao((e.situacao as '' | 'ativo' | 'inativo') ?? 'ativo')),
    ordenacao: ord,
  });
  const carga = useCarga(() => api.get<Parceiro[]>(`/api/parceiros${consulta({ busca: busca.trim(), papel })}`), [busca, papel]);
  const todos = carga.dados ?? [];
  const conta = (s: string) => todos.filter((g) => g.status === s).length;
  const principal = (g: Parceiro) => g.entidades.find((e) => e.status === 'ativo') ?? g.entidades[0];
  const lista = ord.ordenar(
    todos.filter((g) => !situacao || g.status === situacao),
    {
      nome: (g) => g.nome,
      papeis: (g) => g.papeis.join(' · '),
      entidades: (g) => g.entidades.length,
      cnpj: (g) => principal(g)?.cnpj ?? '',
      contato: (g) => principal(g)?.contato ?? '',
      comissao: (g) => g.comissao_percentual ?? -1,
      situacao: (g) => g.status,
    },
  );
  return (
    <>
      <div className="titulo-pag-linha">
        <span className="linha-totais" data-teste="resumo-parceiros">
          <b>{carga.dados ? lista.length : '…'}</b> parceiro(s) comercial(is)
        </span>
        <Ajuda titulo="Parceiro comercial">Nas telas, “Parceiro comercial”; para o CNPJ específico, “Entidade faturadora”; no Financeiro, “Favorecido”. Uma empresa com vários papéis tem um único cadastro.</Ajuda>
        <span className="espaco" />
        {pode('criar', 'parceiro') && (
          <button type="button" className="botao-primario" onClick={() => setNovo(true)} data-teste="novo-parceiro">
            <Icone nome="mais" /> Novo parceiro
          </button>
        )}
      </div>
      <BarraFiltros
        teste="parceiros"
        direita={pers.controles}
        fixos={
          <>
            <CampoBusca valor={busca} setValor={setBusca} rotulo="Buscar parceiro" ajuda="Nome, CNPJ, e-mail ou telefone" dica="Nome, razão social, CNPJ (com ou sem máscara), e-mail ou telefone" teste="busca-parceiros" total={busca.trim() ? lista.length : null} />
            <label>
              Status
              <select value={situacao} onChange={(e) => setSituacao(e.target.value as '' | 'ativo' | 'inativo')} data-teste="filtro-situacao-parceiro">
                <option value="ativo">Ativos ({conta('ativo')})</option>
                <option value="inativo">Inativos ({conta('inativo')})</option>
                <option value="">Todos ({todos.length})</option>
              </select>
            </label>
            <label>
              Papel
              <select value={papel} onChange={(e) => setPapel(e.target.value as '' | Papel)} data-teste="filtro-papel">
                <option value="">Todos</option>
                {PAPEIS.map((p) => (
                  <option key={p}>{p}</option>
                ))}
              </select>
            </label>
          </>
        }
        aoLimpar={() => (setSituacao('ativo'), setPapel(''), setBusca(''))}
      />
      <EstadoCarga carregando={carga.carregando && !carga.dados} erro={carga.erro} recarregar={carga.recarregar} teste="parceiros" />
      {carga.dados &&
        (lista.length === 0 ? (
          <Estado tipo="vazio" titulo={busca.trim() || papel || (situacao && todos.length) ? 'nenhum parceiro comercial com estes filtros' : 'nenhum parceiro comercial cadastrado'} teste="parceiros-vazio" />
        ) : (
          <div className="rolagem quadro-tabela">
            <table ref={pers.tabela} className="tabela" data-teste="lista-grupos">
              <thead>
                <tr>
                  <Th ord={ord} coluna="nome" className="col-fixa">
                    Parceiro comercial
                  </Th>
                  <Th ord={ord} coluna="papeis">Papéis</Th>
                  <Th ord={ord} coluna="entidades" numero titulo="Entidades faturadoras (CNPJ)">
                    Entidades
                  </Th>
                  <Th ord={ord} coluna="cnpj">CNPJ principal</Th>
                  <Th ord={ord} coluna="contato">Contato</Th>
                  <Th ord={ord} coluna="comissao" numero>Comissão padrão</Th>
                  <Th ord={ord} coluna="situacao">Status</Th>
                </tr>
              </thead>
              <tbody>
                {lista.map((g) => {
                  const e = principal(g);
                  return (
                    <tr key={g.id} data-teste={`parceiro-${g.id}`}>
                      <td className="col-fixa">
                        <a href={`#/administracao/parceiros/${g.id}`}>{g.nome}</a>
                      </td>
                      <td>{g.papeis.join(' · ')}</td>
                      <td className="numero" title={g.entidades.map((x) => x.nome_fantasia || x.razao_social).join(', ')}>
                        {g.entidades.length}
                      </td>
                      <td className="nowrap">{e?.cnpj ?? <span className="ausente">—</span>}</td>
                      <td>
                        {e?.contato || e?.email ? (
                          <>
                            {e.contato}
                            {e.email && <div className="texto-secundario">{e.email}</div>}
                          </>
                        ) : (
                          <span className="ausente">—</span>
                        )}
                      </td>
                      <td>{g.comissao_regra ? `${regraComissao(g.comissao_regra).nome} · ${pctNum(g.comissao_percentual ?? 0)}` : <span className="ausente">—</span>}</td>
                      <td>
                        <SeloSituacao texto={rotuloStatus(g.status)} tom={g.status === 'ativo' ? 'positivo' : 'encerrado'} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="rodape-tabela">{lista.length === 1 ? '1 parceiro' : `${lista.length} parceiros`}</div>
          </div>
        ))}
      {novo && <ModalParceiro aoFechar={() => setNovo(false)} aoSalvar={(p) => (setNovo(false), navegar(`/administracao/parceiros/${p.id}`))} />}
    </>
  );
}

interface LinhaHistorico {
  data_hora: string;
  usuario: string | null;
  texto: string;
  justificativa: string | null;
}

export function ParceiroDetalhe({ id }: { id: string }) {
  const { pode } = useSessao();
  const [editar, setEditar] = useState(false);
  const [status, setStatus] = useState(false);
  const [adicionarRegistro, setAdicionarRegistro] = useState(false);
  const [entidade, setEntidade] = useState<EntidadeParceiro | 'nova' | null>(null);
  const carga = useCarga(() => Promise.all([api.get<Parceiro>(`/api/parceiros/${id}`), api.get<LinhaHistorico[]>(`/api/parceiros/${id}/historico`)]), [id]);
  useRotuloTrilha(carga.dados?.[0].nome);
  if (!carga.dados) return <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} teste="parceiro" />;
  const [g, historico] = carga.dados;
  const ativo = g.status === 'ativo';
  const podeEditar = pode('editar', 'parceiro') && ativo;
  const locacao = g.papeis.includes('Parceiro de Locação');
  const temCondicoes = CAMPOS_CONDICOES_LOCACAO.some((c) => g[`cond_${c.chave}`]) || g.reajuste_padrao;
  const recarregar = () => void carga.recarregar();
  return (
    <>
      <section className="resumo-destaque" data-teste="resumo-parceiro">
        <div className="cabecalho-registro">
          <h1 className="titulo-pagina">{g.nome}</h1>
          <div className="selos-cabecalho">
            <SeloSituacao texto={rotuloStatus(g.status)} tom={ativo ? 'positivo' : 'encerrado'} />
            {g.papeis.map((p) => (
              <span key={p} className="selo selo-neutro">
                {p}
              </span>
            ))}
          </div>
        </div>
        <p className="sem-margem texto-secundario">{g.observacoes}</p>
        <div className="acoes-direita barra-acoes-registro">
          {podeEditar && (
            <button type="button" className="botao-secundario" onClick={() => setEditar(true)} data-teste="editar-parceiro">
              Editar
            </button>
          )}
          {podeEditar && (
            <button type="button" className="botao-secundario" onClick={() => setEntidade('nova')} data-teste="adicionar-entidade">
              Adicionar entidade/CNPJ
            </button>
          )}
          {ativo && pode('editar', 'oportunidade') && (
            <button type="button" className="botao-secundario" onClick={() => setAdicionarRegistro(true)} data-teste="adicionar-registro-parceiro">
              Adicionar Registro da Oportunidade
            </button>
          )}
          {pode('inativar', 'parceiro') && (
            <button type="button" className="botao-secundario" onClick={() => setStatus(true)} data-teste={ativo ? 'inativar-parceiro' : 'reativar-parceiro'}>
              {ativo ? 'Inativar' : 'Reativar'}
            </button>
          )}
        </div>
      </section>

      <div className="grade-2">
        <section className="cartao" data-teste="comissao-padrao">
          <h2>
            Comissão padrão
            <Ajuda titulo="Comissão padrão">Herdada pela Proposta quando este parceiro é escolhido. Alterar na Proposta exige autorização e fica na auditoria; a versão aceita congela a regra.</Ajuda>
          </h2>
          {g.comissao_regra ? (
            <dl className="dados">
              <dt>Regra</dt>
              <dd>{regraComissao(g.comissao_regra).nome}</dd>
              <dt>Percentual</dt>
              <dd>{pctNum(g.comissao_percentual ?? 0)}</dd>
              <dt>Base de cálculo</dt>
              <dd>{regraComissao(g.comissao_regra).base}</dd>
              <dt>Frequência/momento</dt>
              <dd>{regraComissao(g.comissao_regra).frequencia}</dd>
            </dl>
          ) : (
            <Estado tipo="vazio" titulo="sem regra padrão cadastrada (use Editar)" />
          )}
        </section>
        {locacao && (
          <section className="cartao" data-teste="condicoes-padrao-locacao">
            <h2>Condições padrão de Locação</h2>
            {temCondicoes ? (
              <dl className="dados dados-largos">
                {CAMPOS_CONDICOES_LOCACAO.map((c) => (
                  <div key={c.chave} className="contents">
                    <dt>{c.rotulo}</dt>
                    <dd>{c.chave === 'reajuste' && g.reajuste_padrao ? textoReajuste(g.reajuste_padrao) : g[`cond_${c.chave}`] || '—'}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <Estado tipo="vazio" titulo="sem condições padrão (use Editar)" />
            )}
            <p className="texto-secundario">Ao escolher este parceiro na Proposta de Locação, estas condições são trazidas automaticamente e podem ser editadas naquela versão.</p>
          </section>
        )}
        {locacao && <QuadroRegraCobranca regra={g.regra_cobranca ?? null} titulo="Regra de cobrança da Locação" origem={g.regra_cobranca ? undefined : 'nenhuma'} />}
        <QuadroRegraRecebimento regra={g.regra_recebimento ?? regraRecebimentoVazia()} titulo="Recebimento da comissão" nota="Copiada para cada Proposta no envio; alterar aqui não muda operações já enviadas." />
      </div>

      <h2>Entidades faturadoras ({g.entidades.length})</h2>
      {g.entidades.length ? (
        <div className="rolagem">
          <table className="tabela tabela-compacta" data-teste="entidades">
            <thead>
              <tr>
                <th>Razão social</th>
                <th>Nome fantasia</th>
                <th>CNPJ</th>
                <th>Inscrição estadual</th>
                <th>UF</th>
                <th>Endereço</th>
                <th>Código da revenda Inttrix</th>
                <th>Contato</th>
                <th>Status</th>
                <th>Condições usuais</th>
                {podeEditar && <th className="col-acoes">Ação</th>}
              </tr>
            </thead>
            <tbody>
              {g.entidades.map((e) => (
                <tr key={e.id}>
                  <td>{e.razao_social}</td>
                  <td>{e.nome_fantasia}</td>
                  <td className="nowrap">{e.cnpj}</td>
                  <td className="nowrap">{e.ie}</td>
                  <td>{e.uf}</td>
                  <td>{e.endereco}</td>
                  <td>{e.codigo_revenda}</td>
                  <td>
                    {e.contato}
                    <div className="texto-secundario">{[e.email, e.telefone].filter(Boolean).join(' · ')}</div>
                  </td>
                  <td>{rotuloStatus(e.status, true)}</td>
                  <td>{e.condicoes ?? '—'}</td>
                  {podeEditar && (
                    <td className="col-acoes">
                      <button type="button" className="botao-secundario pequeno" onClick={() => setEntidade(e)}>
                        Editar
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <Estado tipo="vazio" titulo="nenhuma entidade faturadora (use Adicionar entidade/CNPJ)" />
      )}

      {pode('visualizar', 'oportunidade') && <RegistrosDoParceiro parceiroId={g.id} adicionar={adicionarRegistro} aoFecharAdicionar={() => setAdicionarRegistro(false)} />}

      <h2>Histórico do cadastro</h2>
      <ul className="linha-tempo" data-teste="historico-parceiro">
        {historico.map((h, i) => (
          <li key={i}>
            <strong>{dataHora(h.data_hora)}</strong> · {h.texto}
            {h.justificativa ? `: ${h.justificativa}` : ''} <span className="texto-secundario">({h.usuario ?? 'sistema'})</span>
          </li>
        ))}
      </ul>
      {editar && <ModalParceiro parceiro={g} aoFechar={() => setEditar(false)} aoSalvar={() => (setEditar(false), recarregar())} />}
      {status && <ModalStatus parceiro={g} aoFechar={() => setStatus(false)} aoSalvar={() => (setStatus(false), recarregar())} />}
      {entidade && <ModalEntidade parceiro={g} entidade={entidade === 'nova' ? undefined : entidade} aoFechar={() => setEntidade(null)} aoSalvar={() => (setEntidade(null), recarregar())} />}
    </>
  );
}

type Condicoes = Partial<Record<CampoCondicao, string>>;

/** Novo parceiro comercial / Editar (papéis, comissão padrão com base e frequência separadas, condições de Locação). */
function ModalParceiro({ parceiro, aoFechar, aoSalvar }: { parceiro?: Parceiro; aoFechar: () => void; aoSalvar: (p: Parceiro) => void }) {
  const [nome, setNome] = useState(parceiro?.nome ?? '');
  const [papeis, setPapeis] = useState<Papel[]>(parceiro?.papeis ?? []);
  const [observacoes, setObservacoes] = useState(parceiro?.observacoes ?? '');
  const [comissao, setComissao] = useState<{ regra: IdRegraComissao; percentual: number } | null>(parceiro?.comissao_regra ? { regra: parceiro.comissao_regra, percentual: parceiro.comissao_percentual ?? 0 } : null);
  const [cond, setCond] = useState<Condicoes>(() => Object.fromEntries(CAMPOS_CONDICOES_LOCACAO.map((c) => [c.chave, parceiro?.[`cond_${c.chave}`] ?? ''])));
  const [reajuste, setReajuste] = useState<Reajuste>(parceiro?.reajuste_padrao ?? { tipo: 'Sem reajuste' });
  const [regraCobranca, setRegraCobranca] = useState<RegraCobrancaLocacao>(parceiro?.regra_cobranca ?? regraCobrancaVazia());
  const [regraRecebimento, setRegraRecebimento] = useState<RegraRecebimento>(parceiro?.regra_recebimento ?? regraRecebimentoVazia());
  const [justificativa, setJustificativa] = useState('');
  const [faltam, setFaltam] = useState<string[]>([]);
  const [salvando, setSalvando] = useState(false);
  // Como na DEMO-03: nome já cadastrado orienta a acrescentar o papel no cadastro existente (o servidor também recusa).
  const existentes = useCarga(() => api.get<Parceiro[]>('/api/parceiros'), []);
  const repetido = (existentes.dados ?? []).find((x) => x.id !== parceiro?.id && nome.trim() && x.nome.trim().toLowerCase() === nome.trim().toLowerCase());
  const mudouComissao = Boolean(parceiro) && ((comissao?.regra ?? null) !== parceiro!.comissao_regra || (comissao?.percentual ?? null) !== (parceiro!.comissao_percentual ?? null));
  const alternarPapel = (p: Papel) => setPapeis(papeis.includes(p) ? papeis.filter((x) => x !== p) : PAPEIS.filter((x) => x === p || papeis.includes(x)));
  const salvar = async () => {
    const f = [!nome.trim() && 'Informe o nome do parceiro comercial.', repetido && `Já existe o parceiro ${repetido.nome}: adicione o papel no cadastro existente em vez de duplicar.`, papeis.length === 0 && 'Escolha ao menos um papel.', comissao && !(comissao.percentual >= 0 && comissao.percentual < 100) && 'Percentual de comissão entre 0 e 100.', parceiro && mudouComissao && !justificativa.trim() && 'Alterar a comissão padrão exige justificativa (auditoria).', parceiro?.origem_sistema && !mudouComissao && !justificativa.trim() && 'Informe a justificativa: registro migrado da planilha.'].filter(Boolean) as string[];
    setFaltam(f);
    if (f.length) return;
    setSalvando(true);
    try {
      const corpo = {
        nome,
        papeis,
        observacoes,
        comissao,
        condicoes_locacao: papeis.includes('Parceiro de Locação') ? { ...cond, reajuste_padrao: reajuste } : undefined,
        ...(papeis.includes('Parceiro de Locação') ? { regra_cobranca: regraCobranca } : {}),
        regra_recebimento: regraRecebimento,
        justificativa: justificativa.trim() || undefined,
      };
      const p = parceiro ? await api.patch<Parceiro>(`/api/parceiros/${parceiro.id}`, { ...corpo, atualizado_em: parceiro.atualizado_em }) : await api.post<Parceiro>('/api/parceiros', corpo);
      aoSalvar(p);
    } catch (e) {
      setFaltam([(e as Error).message]);
    } finally {
      setSalvando(false);
    }
  };
  return (
    <Modal
      titulo={parceiro ? `Editar — ${parceiro.nome}` : 'Novo parceiro comercial'}
      aoFechar={aoFechar}
      largo
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Cancelar
          </button>
          <button type="button" className="botao-primario" onClick={salvar} disabled={salvando} data-teste="salvar-parceiro">
            {parceiro ? 'Salvar alterações' : 'Cadastrar parceiro comercial'}
          </button>
        </>
      }
    >
      <div className="grade-form sem-borda">
        <label className="coluna-dupla">
          Nome do parceiro comercial
          <input value={nome} onChange={(e) => setNome(e.target.value)} data-teste="nome-parceiro" />
        </label>
        <fieldset className="opcoes grupo-radio largura-total" data-teste="papeis-parceiro">
          <legend>Papéis (uma empresa pode ter vários, sem cadastro duplicado)</legend>
          {PAPEIS.map((p) => (
            <label key={p} className="opcao">
              <input type="checkbox" checked={papeis.includes(p)} onChange={() => alternarPapel(p)} /> {p}
            </label>
          ))}
        </fieldset>
        <label className="largura-total">
          Observações
          <input value={observacoes} onChange={(e) => setObservacoes(e.target.value)} />
        </label>
        <div className="subtitulo-form largura-total">
          Comissão padrão
          <Ajuda titulo="Regra de comissão">A regra separa a BASE de cálculo (parcela, total do projeto ou total da venda) da FREQUÊNCIA do pagamento (por parcela, mensal ou uma única vez).</Ajuda>
        </div>
        <label className="coluna-dupla">
          Regra
          <select value={comissao?.regra ?? ''} onChange={(e) => setComissao(e.target.value ? { regra: e.target.value as IdRegraComissao, percentual: comissao?.percentual ?? 10 } : null)} data-teste="regra-comissao-parceiro">
            <option value="">— sem regra padrão —</option>
            {REGRAS_COMISSAO.map((r) => (
              <option key={r.id} value={r.id}>
                {r.nome}
              </option>
            ))}
          </select>
        </label>
        <label>
          Percentual (%)
          <input type="number" min={0} max={99} step="0.1" value={comissao?.percentual ?? ''} disabled={!comissao} onChange={(e) => comissao && setComissao({ ...comissao, percentual: Number(e.target.value) })} data-teste="percentual-comissao-parceiro" />
        </label>
        {comissao && (
          <p className="largura-total texto-secundario sem-margem" data-teste="base-frequencia">
            Base de cálculo: <strong>{regraComissao(comissao.regra).base}</strong> · Frequência/momento: <strong>{regraComissao(comissao.regra).frequencia}</strong>
          </p>
        )}
        {parceiro && (mudouComissao || parceiro.origem_sistema) && (
          <label className="largura-total">
            {mudouComissao ? 'Justificativa da alteração da comissão (obrigatória — ação sensível)' : 'Justificativa (obrigatória: registro migrado da planilha)'}
            <input value={justificativa} onChange={(e) => setJustificativa(e.target.value)} data-teste="justificativa-comissao" />
          </label>
        )}
        <div className="subtitulo-form largura-total" data-teste="secao-recebimento">
          Recebimento da comissão
          <Ajuda titulo="Recebimento da comissão">Preencha uma vez por parceiro: a regra é copiada para a Proposta no envio e chega ao Financeiro com a operação. Em branco = “A confirmar”: nada é calculado por suposição. Alterar aqui não muda operações já enviadas.</Ajuda>
        </div>
        <EditorRegraRecebimento valor={regraRecebimento} aoMudar={setRegraRecebimento} papeis={papeis} regraComissao={comissao?.regra ?? null} />
        {papeis.includes('Parceiro de Locação') && (
          <>
            <div className="subtitulo-form largura-total">Condições padrão de Locação</div>
            {CAMPOS_CONDICOES_LOCACAO.map((c) =>
              c.chave === 'reajuste' ? (
                // Reajuste padrão estruturado por tipo (herdado pela oferta de Locação); o texto acompanha para documentos.
                <div key={c.chave} className="largura-total bloco-reajuste" data-teste="cond-reajuste">
                  <div className="rotulo-campo">Reajuste padrão</div>
                  <EditorReajuste valor={reajuste} aoMudar={setReajuste} teste="cond-reajuste-editor" />
                </div>
              ) : (
                <label key={c.chave} className={c.chave === 'observacoes' || c.chave === 'responsabilidades' ? 'largura-total' : undefined}>
                  {c.rotulo}
                  <input value={cond[c.chave] ?? ''} onChange={(e) => setCond({ ...cond, [c.chave]: e.target.value })} data-teste={`cond-${c.chave}`} />
                </label>
              ),
            )}
            <div className="subtitulo-form largura-total">
              Regra de cobrança da Locação
              <Ajuda titulo="Regra de cobrança">Regra do contrato deste parceiro (não é padrão universal). Deixe em branco o que o contrato não define: aparece como “A confirmar” e nada é projetado por suposição.</Ajuda>
            </div>
            <EditorRegraCobranca valor={regraCobranca} aoMudar={setRegraCobranca} />
          </>
        )}
      </div>
      {faltam.length > 0 && <Estado tipo="bloqueado" titulo="Falta completar" itens={faltam} teste="faltam-parceiro" />}
    </Modal>
  );
}

function ModalStatus({ parceiro: g, aoFechar, aoSalvar }: { parceiro: Parceiro; aoFechar: () => void; aoSalvar: () => void }) {
  const inativar = g.status === 'ativo';
  const [justificativa, setJustificativa] = useState('');
  const [erro, setErro] = useState('');
  const confirmar = async () => {
    try {
      await api.post(`/api/parceiros/${g.id}/${inativar ? 'inativar' : 'reativar'}`, { justificativa });
      aoSalvar();
    } catch (e) {
      setErro((e as Error).message);
    }
  };
  return (
    <Modal
      titulo={`${inativar ? 'Inativar' : 'Reativar'} — ${g.nome}`}
      aoFechar={aoFechar}
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button type="button" className="botao-primario" disabled={!justificativa.trim()} onClick={confirmar} data-teste="confirmar-inativar">
            {inativar ? 'Confirmar inativação' : 'Confirmar reativação'}
          </button>
        </>
      }
    >
      {inativar && <p className="aviso aviso-info">Inativar não apaga nada: Propostas, Pedidos, Ordens e registros continuam com o histórico. O parceiro deixa de aparecer para novas escolhas.</p>}
      {erro && (
        <p className="aviso aviso-erro" role="alert">
          {erro}
        </p>
      )}
      <label className="campo">
        Justificativa (obrigatória)
        <input value={justificativa} onChange={(e) => setJustificativa(e.target.value)} data-teste="justificativa-inativar" />
      </label>
    </Modal>
  );
}

/** Adicionar entidade/CNPJ (ou editar uma existente, sem mudar o CNPJ). */
function ModalEntidade({ parceiro: g, entidade, aoFechar, aoSalvar }: { parceiro: Parceiro; entidade?: EntidadeParceiro; aoFechar: () => void; aoSalvar: () => void }) {
  const { aceitarFicticio } = useAmbiente();
  const vazio = { cnpj: '', razao_social: '', nome_fantasia: '', ie: '', uf: '', endereco: '', codigo_revenda: '', contato: '', email: '', telefone: '', condicoes: '', status: 'ativo' };
  const [e, setE] = useState<Record<string, string>>(() => (entidade ? Object.fromEntries(Object.keys(vazio).map((k) => [k, String((entidade as unknown as Record<string, unknown>)[k] ?? '')])) : vazio));
  const [faltam, setFaltam] = useState<string[]>([]);
  const [justificativa, setJustificativa] = useState('');
  const [cnpjValido, setCnpjValido] = useState(false);
  const existente = useCnpjExistente(e.cnpj, cnpjValido);
  const salvar = async () => {
    // Como na DEMO-03: todas as pendências juntas antes de enviar (o servidor confere de novo).
    const f = entidade ? (entidade.origem_sistema && !justificativa.trim() ? ['Informe a justificativa: registro migrado da planilha.'] : []) : ([!e.razao_social.trim() && 'Informe a razão social.', !e.uf && 'Informe a UF.', existente?.cadastrado && `O CNPJ ${e.cnpj} já está cadastrado em ${existente.nome}: não cadastre de novo.`].filter(Boolean) as string[]);
    setFaltam(f);
    if (f.length) return;
    try {
      if (entidade) {
        const { cnpj: _c, ...dados } = e;
        await api.patch(`/api/parceiros/${g.id}/entidades/${entidade.id}`, { ...dados, justificativa: justificativa.trim() || undefined, atualizado_em: entidade.atualizado_em });
      } else await api.post(`/api/parceiros/${g.id}/entidades`, e);
      aoSalvar();
    } catch (x) {
      setFaltam([(x as Error).message]);
    }
  };
  const campo = (k: string, rotulo: string, largo = false) => (
    <label className={largo ? 'coluna-dupla' : undefined}>
      {rotulo}
      <input value={e[k]} onChange={(ev) => setE({ ...e, [k]: ev.target.value })} data-teste={`entidade-${k}`} />
    </label>
  );
  return (
    <Modal
      titulo={entidade ? `Editar entidade/CNPJ — ${entidade.razao_social}` : `Adicionar entidade/CNPJ — ${g.nome}`}
      aoFechar={aoFechar}
      largo
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Cancelar
          </button>
          <button type="button" className="botao-primario" onClick={salvar} data-teste="salvar-entidade">
            {entidade ? 'Salvar entidade' : 'Adicionar entidade'}
          </button>
        </>
      }
    >
      <div className="grade-form sem-borda">
        <div className="coluna-dupla">
          {entidade ? (
            <label>
              CNPJ
              <input value={e.cnpj} disabled />
              <small className="ajuda">O CNPJ não muda: para corrigir, inative esta entidade e cadastre a correta.</small>
            </label>
          ) : (
            <CampoCnpj valor={e.cnpj} aoMudar={(v, valido) => (setE({ ...e, cnpj: v }), setCnpjValido(valido))} teste="cnpj-entidade-parceiro" obrigatorio aceitarFicticio={aceitarFicticio} />
          )}
        </div>
        {campo('razao_social', 'Razão social', true)}
        {campo('nome_fantasia', 'Nome fantasia')}
        {campo('ie', 'Inscrição estadual')}
        <SeletorUf valor={e.uf} aoMudar={(uf) => setE({ ...e, uf })} teste="uf-entidade-parceiro" />
        {campo('endereco', 'Endereço', true)}
        {campo('codigo_revenda', 'Código da revenda Inttrix')}
        {campo('contato', 'Contato')}
        {campo('email', 'E-mail')}
        {campo('telefone', 'Telefone')}
        {campo('condicoes', 'Condições usuais', true)}
        {entidade && (
          <label>
            Status
            <select value={e.status} onChange={(ev) => setE({ ...e, status: ev.target.value })}>
              <option value="ativo">Ativa</option>
              <option value="inativo">Inativa</option>
            </select>
          </label>
        )}
        {entidade?.origem_sistema && (
          <label className="coluna-dupla">
            Justificativa (obrigatória: registro migrado da planilha)
            <textarea rows={2} value={justificativa} onChange={(ev) => setJustificativa(ev.target.value)} maxLength={1000} data-teste="justificativa-entidade-parceiro" />
          </label>
        )}
      </div>
      {faltam.length > 0 && <Estado tipo="bloqueado" titulo="Falta completar" itens={faltam} teste="faltam-entidade" />}
      <p className="texto-secundario">Se o CNPJ já existir em outro cadastro, o HUB mostra onde está: o mesmo CNPJ não é cadastrado duas vezes — acrescente o papel no cadastro existente.</p>
    </Modal>
  );
}

type RegistroParceiro = Registro & { oportunidade_numero: string; oportunidade_titulo: string; papel: string };
/** Registros da Oportunidade em que o parceiro é parceiro comercial ou fabricante — uma única fonte, sem duplicação. */
function RegistrosDoParceiro({ parceiroId, adicionar, aoFecharAdicionar }: { parceiroId: string; adicionar: boolean; aoFecharAdicionar: () => void }) {
  const carga = useCarga(() => Promise.all([api.get<RegistroParceiro[]>(`/api/parceiros/${parceiroId}/registros`), api.get<OpcoesOportunidade>('/api/oportunidades/opcoes')]), [parceiroId]);
  if (!carga.dados) return <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} />;
  const [registros, opcoes] = carga.dados;
  const faixas = opcoes.faixas_registro;
  return (
    <section data-teste="registros-parceiro-secao">
      <h2>Registros da Oportunidade neste parceiro ({registros.length})</h2>
      {registros.length ? (
        <div className="rolagem">
          <table className="tabela tabela-compacta" data-teste="registros-parceiro">
            <thead>
              <tr>
                <th>Número</th>
                <th>Oportunidade</th>
                <th>Fabricante</th>
                <th>Papel no registro</th>
                <th>Vencimento</th>
                <th>Alerta</th>
              </tr>
            </thead>
            <tbody>
              {registros.map((r) => (
                <tr key={r.id} className={faixas ? `prioridade-${statusRegistro(r, opcoes.hoje, faixas).nivel}` : ''}>
                  <td>{r.numero}</td>
                  <td>
                    <a href={`#/oportunidades/${r.oportunidade_id}`}>
                      #{r.oportunidade_numero} — {r.oportunidade_titulo}
                    </a>
                  </td>
                  <td>{r.fabricante || '—'}</td>
                  <td>{r.papel}</td>
                  <td className="nowrap">
                    <strong>{dataBr(r.data_vencimento)}</strong>
                  </td>
                  <td>{faixas ? <SeloRegistro r={r} faixas={faixas} hoje={opcoes.hoje} curto vencimento="oculto" /> : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <Estado tipo="vazio" titulo="nenhum registro neste parceiro" />
      )}
      <p className="texto-secundario">É o mesmo registro exibido na Oportunidade (uma única fonte, sem duplicação).</p>
      {adicionar && <ModalNovoRegistro parceiroId={parceiroId} aoFechar={aoFecharAdicionar} aoSalvar={() => (aoFecharAdicionar(), void carga.recarregar())} />}
    </section>
  );
}
